/**
* Renders locale-specific deep content block for /about, /contact,
* /coverage pages across non-Thai locales. Consumes DeepPageBlock
* from locale-page-deep.ts and outputs SSR-friendly markup that
* lifts word-count above the A5 threshold with genuinely unique copy.
*/
import type { DeepPageBlock } from "@/data/locale-page-deep";

export function LocalePageDeepBlock({ block: b }: { block: DeepPageBlock }) {
  return (
    <>
      <section className="mx-auto max-w-5xl px-4 py-14">
        <p className="text-xs font-semibold uppercase tracking-widest text-[var(--gold)]">
          {b.kicker}
        </p>
        <h2 className="mt-2 text-2xl font-bold text-foreground md:text-3xl">{b.heading}</h2>
        <p className="mt-4 text-[15px] leading-relaxed text-foreground/90">{b.intro}</p>
        <p className="mt-3 text-[15px] leading-relaxed text-foreground/90">{b.intro2}</p>
      </section>

      <section className="border-y border-border bg-muted/30 py-14">
        <div className="mx-auto max-w-6xl px-4">
          <h2 className="text-2xl font-bold text-foreground">{b.cardsHeading}</h2>
          <div className="mt-6 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {b.cards.map((c) => (
              <article
                key={c.title}
                className="rounded-xl border border-border bg-card p-5"
              >
                <h3 className="text-base font-semibold text-foreground">{c.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-foreground/85">{c.body}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-5xl px-4 py-14">
        <h2 className="text-2xl font-bold text-foreground">{b.factsHeading}</h2>
        <ul className="mt-6 grid gap-3 md:grid-cols-2">
          {b.facts.map((f) => (
            <li
              key={f}
              className="flex gap-2 rounded-lg border border-[var(--gold)]/40 bg-[var(--gold)]/[0.06] p-3 text-sm text-foreground"
            >
              <span className="mt-0.5 text-[var(--gold)]">◆</span>
              <span>{f}</span>
            </li>
          ))}
        </ul>
        <p className="mt-8 text-[15px] leading-relaxed text-foreground/90" data-speakable>
          {b.closing}
        </p>
      </section>
    </>
  );
}
